import { createSelector } from "@reduxjs/toolkit";
import { flatMap } from "lodash";
import map from "lodash/map";
import uniq from "lodash/uniq";

import type { TCommodity } from "@/util/commodity";

import { endpoints as onboardingEndpoints } from "../../routes/_authenticated/onboarding/-api/onboarding";
import { endpoints as accountEndpoints } from "../../shared/api/account";
import { endpoints as londonFixEndpoints } from "../../shared/api/londonFix";
import { endpoints as policyEndpoints } from "../../shared/api/policy";
import type { RootState } from "../store";
import type { TAppSliceState } from "./appTypes";

export const selectApp = (state: RootState): TAppSliceState => state.app;

export const selectCognitoId = createSelector(selectApp, (appState) => appState.emulateUser || appState.cognitoId);

export const selectPolicy = (state: RootState) => {
  const cognitoId = selectCognitoId(state);

  return cognitoId ? policyEndpoints.getPolicyForCognitoUser.select(cognitoId)(state)?.data : undefined;
};

export const selectAccountList = createSelector(selectPolicy, (policy) => {
  if (!policy) return [];
  return uniq(
    map(
      policy.aclGroups.filter((group) => group.group !== "onboarding" && group.account !== "*"),
      "account",
    ),
  );
});

export const selectOnboardingSessionIds = createSelector(selectPolicy, (policy) => {
  if (!policy) return [];
  const sessions = map(
    policy.aclGroups.filter((group) => group.group === "onboarding"),
    "account",
  );
  return sessions;
});

// TODO: we may need to allow this to be configurable in the future
export const selectActiveOnboardingSessionId = createSelector(selectOnboardingSessionIds, (sessions) => sessions[0]);

export const selectActiveOnboardingSession = createSelector(
  selectActiveOnboardingSessionId,
  selectCognitoId,
  (state: RootState) => state,
  (sessionId, cognitoId, state) => {
    if (!cognitoId || !sessionId) return undefined;
    return onboardingEndpoints.getOnboardingSession.select({ cognitoId, sessionId })(state)?.data;
  },
);

export const selectActiveOnboardingType = createSelector(
  selectActiveOnboardingSession,
  (session) => session?.onboardingType,
);

export const selectActiveOnboardingDocuments = createSelector(
  selectActiveOnboardingSession,
  (session) => session?.documents ?? [],
);

export const selectAppSettings = createSelector(selectPolicy, (policy) => policy?.settings);

export const selectGlobalBanner = createSelector(selectAppSettings, (appSettings) => appSettings?.globalBanner);

export const selectLysaRate = createSelector(selectAppSettings, (appSettings) => appSettings?.lysaRate);

export const selectLysaRateTooltip = createSelector(selectAppSettings, (appSettings) => appSettings?.lysaRateTooltip);

// right now we are only using gold lysa rate and this is coming from the appSettings
// in the near future this should come from a goldmine endpoint
// JB 2025-03-03
export const selectLysaRateByCommodity = createSelector(
  [selectLysaRate, (_state, commodity: TCommodity) => commodity],
  (lysaRate, commodity) => {
    if (!lysaRate || commodity !== "gold") return 0;

    return lysaRate;
  },
);

export const selectSignInsUntilMfaPrompt = createSelector(
  selectAppSettings,
  (appState) => appState?.signInsUntilMfaPrompt ?? 1,
);

export const selectLockedOut = createSelector(selectPolicy, (policy) => policy?.lockOut?.locked ?? false);

export const selectCookieConsent = createSelector(selectAppSettings, (appState) => appState?.cookieConsent);

export const selectAppMaintenanceStatus = createSelector(
  selectAppSettings,
  (appState) => appState?.maintenance ?? false,
);

export const selectAppIgnoredOpportunities = createSelector(
  selectAppSettings,
  (appState) => appState?.ignoredOpportunities ?? {},
);

export const selectActiveAccountEid = createSelector(
  [selectApp, selectAccountList],
  (appState, accountList): string | null => {
    const storedAccount = appState.activeAccount;

    // If no stored account, return first available account or null
    if (!storedAccount) {
      return accountList[0] ?? null;
    }

    // If stored account is a valid EID in the current account list, return it
    if (accountList.includes(storedAccount)) {
      return storedAccount;
    }

    // If stored account is a numeric index (legacy), convert to EID
    const numericIndex = Number(storedAccount);
    if (!isNaN(numericIndex) && accountList[numericIndex]) {
      return accountList[numericIndex];
    }

    // Fallback to first account if stored account is invalid
    return accountList[0] ?? null;
  },
);

export const selectMaybeActiveAccountEid = createSelector(selectActiveAccountEid, (accountEid) => accountEid ?? "");

export const selectMaybeEmail = createSelector(selectApp, (appState) => appState.email);

export const selectUserName = (state: RootState) => {
  const accountEid = selectMaybeActiveAccountEid(state);

  return accountEndpoints.getAccountInfo.select(accountEid)(state)?.data?.name?.trim() || undefined;
};

export const selectEmulateUser = createSelector(selectApp, (appState) => appState.emulateUser);

export const selectAclGroups = createSelector(selectPolicy, (policy) => policy?.aclGroups ?? []);

// A user who only holds "onboarding" groups has signed up but not completed onboarding,
// so they have no real account access yet (e.g. the MFA enrollment prompt is deferred for them).
export const selectIsOnboardingOnly = createSelector(
  selectAclGroups,
  (aclGroups) => aclGroups.length > 0 && aclGroups.every((group) => group.group === "onboarding"),
);

export const selectIsSystemAdmin = createSelector(selectAclGroups, (appState) =>
  appState.some(({ group, account }) => group === "systemAdmin" && account === "*"),
);

export const selectAclMenu = createSelector(selectPolicy, (policy) => policy?.aclMenus ?? []);

export const selectAclMenusByEid = createSelector([selectAclMenu, (_state, eid: string) => eid], (aclMenu, eid) =>
  flatMap(aclMenu, ({ entity, resource }) => (entity === eid ? resource : [])),
);
export const selectAclMenusByActiveEid = createSelector([selectAclMenu, selectMaybeActiveAccountEid], (aclMenu, eid) =>
  flatMap(aclMenu, ({ entity, resource }) => (entity === eid ? resource : [])),
);

export const selectPageTitle = createSelector(selectApp, (appState) => appState.pageTitle);

export const selectListOfMenuResources = createSelector(
  [selectMaybeActiveAccountEid, selectAclMenu],
  (maybeEid, policyObjs) => {
    const activeMenusOrOnboarding = policyObjs.filter(
      ({ entity, resource }) => entity === maybeEid || resource === "onboarding",
    );

    return uniq(map(activeMenusOrOnboarding, "resource"));
  },
);

export const selectListOfMenuResourcesByActiveEid = createSelector(
  selectMaybeActiveAccountEid,
  selectAclMenu,
  (eid, policyObjs) => selectListOfMenuResources.resultFunc(eid, policyObjs),
);

export const selectActiveAssetClassByPage = createSelector(
  [(_state, page: string) => page, selectApp],
  (page, appState) => appState.activeAssetClassByPage[page],
);

export const selectActiveConversion = createSelector(selectApp, (appState) => appState.activeConversion);
